import { useLocation } from "react-router-dom";

import SwitchAppCheckout from "@switchappgo/switchapp-inline";
import { ICheckoutMessageArgs } from "@switchappgo/switchapp-inline/dist/checkout/types";

import {
  useBalances,
  useDebounce,
  useHookForm,
  useMerchantApiMutation,
  useMerchantApiQuery,
  useMerchantApiQueryWithParams,
  useReactRouterLink,
} from "../../../hooks";
import {
  ICreateBankTransferBeneficiary,
  IHookForm,
  IModalFormWithTableProps,
  IFormWithTableProps,
  IViewBankTransferBeneficiary,
  ISwAppSupportedCountry,
  ISwAppSupportedCurrency,
  IBank,
  NoMeta,
} from "../../../interfaces-and-types";
import { globalHandlerUtils } from "../../../utils";

import { View } from "./View";
import { createBankTransferBeneficiarySchema } from "./validationSchema";
import { useEffect, useState } from "react";

export interface ICreateBankTransferBeneficiaryFormViewProps
  extends IHookForm<ICreateBankTransferBeneficiary>,
    IModalFormWithTableProps {
  isLoadingSupportedCountries: boolean;
  swAppSupportedCountries: ISwAppSupportedCountry[];

  isLoadingSupportedCurrencies: boolean;
  swAppSupportedCurrencies: ISwAppSupportedCurrency[];

  isLoadingBankList: boolean;
  bankList: IBank[];
  formValues: ICreateBankTransferBeneficiary;

  isBankAccountResolved: boolean;
  setBankAccountResolved: (isResolved: boolean) => void;
}

interface ICountrySelect {
  country: string;
}

interface ICountryAndCurrencySelect extends ICountrySelect {
  currency: string;
}

export const CreateBeneficiaryForm = (
  props: IFormWithTableProps & {
    goBackOnClose: boolean; // NOTE: used for on-the-fly product creation eg while creating a payment link
  }
) => {
  const [isBankAccountResolved, setBankAccountResolved] = useState(false);

  const {
    isLoading: isLoadingSupportedCountries,
    // isSuccess: isSuccessSupportedCountries,
    data: swAppSupportedCountries,
  } = useMerchantApiQuery<ISwAppSupportedCountry[]>("getSupportedCountries", {
    enabled: true,
  });

  const { navigate } = useReactRouterLink();
  const location = useLocation();

  const closeModal = () => {
    if (props.goBackOnClose) navigate(-1);
    else navigate(location.pathname.replace("/create/bank", ""));
  };

  const {
    isSuccess: isSuccessCreateBankTransferBeneficiary,
    mutate: createBeneficiary,
  } = useMerchantApiMutation<
    ICreateBankTransferBeneficiary,
    IViewBankTransferBeneficiary
  >("createBankTransferBeneficiary").usePostMutation;

  useEffect(() => {
    if (isSuccessCreateBankTransferBeneficiary) {
      props.refreshTable();
      closeModal();
    }
  }, [isSuccessCreateBankTransferBeneficiary]);

  const initialFormValues: ICreateBankTransferBeneficiary = {
    country: "NG",
    currency: "NGN",
    account_number: "",
    bank_code: "",

    // optional params
    full_name: "",
    email: "",
    phone_number: "",
    description: "",
  };

  const handleApiSubmit = (data: ICreateBankTransferBeneficiary) => {
    createBeneficiary(data);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const { onFormSubmit, errors, register, watch } = useHookForm({
    initialFormValues,
    validationSchema: createBankTransferBeneficiarySchema,
    onValid: handleApiSubmit,
    onInvalid: handleError,
  });

  const formValues = watch();

  const debouncePeriodElapsed = useDebounce();
  const { isLoading: isLoadingBankList, data: bankList } =
    useMerchantApiQueryWithParams<ICountryAndCurrencySelect, IBank[], NoMeta>(
      [
        "getBanks",
        {
          country: formValues.country,
          currency: formValues.currency,
        },
      ],
      {
        enabled:
          debouncePeriodElapsed &&
          !!formValues.country &&
          !!formValues.currency,
      }
    );

  const {
    isLoading: isLoadingSupportedCurrencies,
    data: swAppSupportedCurrencies,
  } = useMerchantApiQueryWithParams<
    ICountrySelect,
    ISwAppSupportedCurrency[],
    NoMeta
  >(
    [
      "getSupportedCurrencies",
      {
        country: formValues.country,
      },
    ],
    {
      enabled: formValues.country !== "",
    }
  );

  return (
    <View
      {...props}
      isModalOpen
      closeModal={closeModal}
      formValues={formValues}
      isLoadingSupportedCountries={isLoadingSupportedCountries}
      swAppSupportedCountries={swAppSupportedCountries || []}
      isLoadingSupportedCurrencies={isLoadingSupportedCurrencies}
      swAppSupportedCurrencies={swAppSupportedCurrencies || []}
      isLoadingBankList={isLoadingBankList}
      bankList={bankList || []}
      isBankAccountResolved={isBankAccountResolved}
      setBankAccountResolved={setBankAccountResolved}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
    />
  );
};
